import { ButtonHTMLAttributes, ReactNode } from "react";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * ボタンのバリアント
   */
  variant?:
    | "primary"
    | "secondary"
    | "outline"
    | "ghost"
    | "black"
    | "outlineGray"
    /** 助けを求める側。青枠。ヒント・解説など、進行を止めない操作 */
    | "help"
    /** AI チューター。紫。AI が答えるものだけに使い、人が書いた導線には使わない */
    /** 取り返しがつかない操作。確認ダイアログの中だけで使う */
    | "destructive";

  /**
   * ボタンのサイズ
   */
  size?: "sm" | "md" | "lg";

  /**
   * ボタンの内容
   */
  children?: ReactNode;

  /**
   * 無効状態
   */
  disabled?: boolean;

  /**
   * 全幅表示
   */
  fullWidth?: boolean;

  /**
   * ローディング状態
   */
  loading?: boolean;

  /**
   * ローディングスピナーの位置
   */
  loadingPosition?: "left" | "right";

  /**
   * 丸いボタン（pill形状）
   */
  rounded?: boolean;

  /**
   * 押し込みの手触りを付ける。
   *
   * 下に同色の濃い縁を敷き、押している間だけその分ボタンを沈める。
   * 縁の高さと沈む量を同じにしてあるので、**下端の位置は動かない。**
   * ずらすと押すたびに次の行が跳ねる。
   *
   * 触覚のフィードバックを求める端末では沈みを止める
   * (prefers-reduced-motion)。縁は残るので見た目は変わらない
   */
  pressable?: boolean;
}

/**
 * 押し込みの深さ。**深さがそのまま重みの序列になる。**
 *
 * 画面でいちばん押してほしいものだけが 4px 沈み、待機している操作は 2px、
 * 進行を止めない操作は沈まない。全部を同じ深さにすると序列が消えて、
 * 押し込み自体が飾りになる。
 *
 * **クラス名は文字列のまま持つ。** テンプレートで組み立てると Tailwind が
 * 拾えず、スタイルが1つも出ない。
 */
/*
Button 以外からも同じ深さを使えるように出す。**数値を写して持たない。**
2 か所に 4px と 2px が書かれると、片方だけ直されて序列が崩れる
*/
export const PRESS_DEPTH = {
  deep: {
    space: "mb-[4px]",
    press: "active:translate-y-[4px]",
  },
  shallow: {
    space: "mb-[2px]",
    press: "active:translate-y-[2px]",
  },
} as const;

/*
縁は box-shadow で描く。**擬似要素を裏に敷く手は使えない。**

before: `after:-z-10` で背の高い面を裏に置いていた。ところが space に
`isolate` が入っていて、要素がスタッキング文脈になる。そうなると負の
z-index の子は要素の「外」へ抜けられず、背景の上に積まれて face 全体を
塗り潰す。実測で、面に bg-primary (#05b45b) を指定したボタンの画素が
primary-600 (#037f40) になっていた。**計算値は明るいのに見た目が暗い、
という形で出るので、getComputedStyle だけ見ていると気づけない。**

box-shadow は border-box の外側に border-radius に沿って描かれるので、
角のところに隙間ができない。押している間は影を消して本体を同じだけ
下げるので、下端の位置は動かない。

**クラス名は文字列のまま持つ。** `shadow-[0_${n}px_...]` と組み立てると
Tailwind が拾えず、影が1つも出ない。
*/
const PRESS_SHADOW: Record<string, { deep: string; shallow: string }> = {
  primary: {
    deep: "shadow-[0_4px_0_0_var(--color-primary-600)]",
    shallow: "shadow-[0_2px_0_0_var(--color-primary-600)]",
  },
  destructive: {
    deep: "shadow-[0_4px_0_0_#991b1b]",
    shallow: "shadow-[0_2px_0_0_#991b1b]",
  },
  black: {
    deep: "shadow-[0_4px_0_0_#404040]",
    shallow: "shadow-[0_2px_0_0_#404040]",
  },
  /* 面が明るい variant は、縁も罫線と同じ濃さにして厚みだけ出す */
  secondary: {
    deep: "shadow-[0_4px_0_0_var(--color-border-main)]",
    shallow: "shadow-[0_2px_0_0_var(--color-border-main)]",
  },
  outline: {
    deep: "shadow-[0_4px_0_0_var(--color-border-main)]",
    shallow: "shadow-[0_2px_0_0_var(--color-border-main)]",
  },
  outlineGray: {
    deep: "shadow-[0_4px_0_0_var(--color-border-main)]",
    shallow: "shadow-[0_2px_0_0_var(--color-border-main)]",
  },
  help: {
    deep: "shadow-[0_4px_0_0_#1d4ed8]",
    shallow: "shadow-[0_2px_0_0_#1d4ed8]",
  },
};

/*
variant ごとの深さ。ghost は面が無いので沈めない (字だけ動いて壊れて見える)。

影で描くようになったので、面が透けている outline でも face は塗られない。
*/
const PRESS_BY_VARIANT: Record<string, keyof typeof PRESS_DEPTH | null> = {
  primary: "deep",
  destructive: "deep",
  black: "deep",
  secondary: "shallow",
  outline: "shallow",
  outlineGray: "shallow",
  help: "shallow",
  ghost: null,
};

// Spinner component for loading state
const Spinner = ({ size }: { size: "sm" | "md" | "lg" }) => {
  const sizeClasses = {
    sm: "w-3 h-3",
    md: "w-3.5 h-3.5",
    lg: "w-4 h-4",
  };

  return (
    <svg
      className={`${sizeClasses[size]} animate-spin`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="32 32"
        opacity="0.25"
      />
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray="16 48"
        opacity="1"
      />
    </svg>
  );
};

export function getButtonClassName({
  variant = "primary",
  size = "md",
  children,
  disabled = false,
  fullWidth = false,
  loading = false,
  rounded = false,
  pressable = false,
  className = "",
}: Pick<
  ButtonProps,
  "variant" | "size" | "children" | "disabled" | "fullWidth" | "loading" | "rounded" | "pressable" | "className"
>): string {
  const isDisabled = disabled || loading;

  /*
  # 動かす性質を数え上げる

  `transition-all` をやめた。all にすると、押したときに変える気の無いものまで
  一緒に動く。**このボタンは押し込みで translate-y と box-shadow を、hover で
  背景色と枠線を変える。** all のままだと、幅や高さが変わる場面 (loading で
  スピナーが入る、ラベルが差し替わる) でも 200ms かけて動いてしまい、
  押した感じが遅れて返る。

  色・影・変形の3つだけに絞る。duration はそのまま 200ms にしている。
  transitions.dev の目盛りには hover のボタンに当たる用途が無く、近い数字が
  あるという理由だけで寄せると、意味の無い付け替えになるため
  */
  const baseClasses =
    `font-sans font-bold ${rounded ? "rounded-full" : "rounded-lg"} transition-[color,background-color,border-color,box-shadow,transform] duration-200 outline-none inline-flex items-center justify-center gap-2 relative${
      pressable && PRESS_BY_VARIANT[variant] ? ` ${PRESS_DEPTH[PRESS_BY_VARIANT[variant]!].space}` : ""
    }`;

  /*
  # 大きさ

  高さは padding だけで決まっていたので、md が 40px、sm が 28px しか
  無かった。**指で押す面としては足りない。** WCAG 2.5.5 (AAA) は 44px、
  2.5.8 (AA) は 24px を求める。字の大きさは変えずに min-h だけを敷いて、
  sm を 36px、md を 44px、lg を 48px にした。

  下限なので、中身が増えた分は今までどおり伸びる。**縮む向きには
  効かない**ので、既にこれより高いボタンの見た目は変わらない。
  */
  const hasChildren = Boolean(children);
  const sizeClasses = {
    sm: hasChildren ? "min-h-9 px-3 py-1.5 max-sm:text-[0.9375rem] text-sm" : "min-h-9 min-w-9 p-1.5 max-sm:text-[0.9375rem] text-sm",
    md: hasChildren ? "min-h-11 px-5 py-2.5 max-sm:text-[0.9375rem] text-sm" : "min-h-11 min-w-11 p-2.5 max-sm:text-[0.9375rem] text-sm",
    lg: hasChildren ? "min-h-12 px-7 py-3.5 text-base" : "min-h-12 min-w-12 p-3.5 text-base",
  };

  // Variant classes with hover, active, focus states
  const variantClasses = {
    // **白文字を #05b45b に乗せると比 2.73 で、AA の 4.5 を大きく割る。**
    // ページでいちばん押してほしいボタンの文字が規格の6割しかなかった。
    // primary-600 (#037f40) なら 5.10 で通る。ダークでも 4.9 台に乗る
    /*
    面はブランドの緑そのまま (#05b45b)。

    以前は primary-600 (#037f40) だった。白い字との比が 5.10:1 で
    WCAG AA を満たす、という理由で一段落としてあったが、**同じサイトの
    一番大きい CTA はすでにブランドの緑を使っていた** (「無料で登録する」
    「無料で学び始める」「30日間無料で試す」「実行」はどれも #05b45b に
    白)。レッスンのボタンだけが暗く、同じ「押してほしい」が 2 色あった。

    比は 2.76:1 に下がる。**字を白のまま明るくした以上、小さい字には
    使わない。** size は md 以上、文言は短く太くする。細かい操作や
    補助の導線は ghost / outline を使う
    */
    primary:
      "bg-primary text-white hover:bg-primary-500 active:bg-primary-500 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    secondary:
      "bg-second text-primary border border-primary hover:bg-third active:bg-third focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    outline:
      "bg-transparent text-primary border border-primary hover:bg-main hover:border-primary active:bg-third active:border-primary focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)] focus:border-primary",
    outlineGray:
      "bg-transparent text-sub border border-gray-200 hover:bg-main hover:border-gray-200 active:bg-third active:border-gray-200 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(0.0,0.0,0.0,0.05)] focus:border-gray-200",
    ghost:
      "bg-transparent text-sub hover:bg-second active:bg-third focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(34,197,94,0.5)]",
    black:
      "bg-black text-white hover:bg-black/90 active:bg-black/90 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(0,0,0,0.5)]",
    help:
      "bg-surface text-blue-700 border-2 border-blue-600 hover:bg-blue-50 active:bg-blue-50 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(37,99,235,0.5)]",
    // **白文字を #dc2626 に乗せると比 4.5 ちょうどで、大きい字しか通らない。**
    // red-700 (#b91c1c) なら 6.0 で本文サイズでも通る
    destructive:
      "bg-red-700 text-white hover:bg-red-800 active:bg-red-800 focus:shadow-[0_0_0_2px_white,0_0_0_4px_rgba(185,28,28,0.5)]",
  };

  /*
  押し込みの厚み。**足す量と引く量を揃えてあるので、下端の位置は動かない。**
  影は場所を取らないので、深さのぶんを mb で先に空けておく。
  押している間は影を消して本体を同じだけ下げる。
  */
  const depthKey = PRESS_BY_VARIANT[variant];
  const depth = depthKey ? PRESS_DEPTH[depthKey] : null;
  const shadow = depthKey ? PRESS_SHADOW[variant]?.[depthKey] : undefined;
  const pressableClass =
    pressable && !isDisabled && depth && shadow
      ? [shadow, depth.press, "active:shadow-none", "motion-reduce:active:translate-y-0"].join(
          " ",
        )
      : "";

  // Width classes
  const widthClass = fullWidth ? "w-full" : "w-auto";

  // Disabled/loading classes
  const disabledClass = isDisabled
    ? "opacity-50 cursor-not-allowed pointer-events-none"
    : "cursor-pointer";

  const buttonClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]} ${pressableClass} ${widthClass} ${disabledClass} ${className}`;

  return buttonClasses;
}

export function Button({
  variant = "primary",
  size = "md",
  children,
  disabled = false,
  fullWidth = false,
  loading = false,
  loadingPosition = "left",
  rounded = false,
  pressable = false,
  className = "",
  ...props
}: ButtonProps) {
  const isDisabled = disabled || loading;
  const buttonClasses = getButtonClassName({
    variant,
    size,
    children,
    disabled,
    fullWidth,
    loading,
    rounded,
    pressable,
    className,
  });

  return (
    <button
      disabled={isDisabled}
      className={buttonClasses}
      style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
      {...props}
    >
      {loading && loadingPosition === "left" && <Spinner size={size} />}
      {children}
      {loading && loadingPosition === "right" && <Spinner size={size} />}
    </button>
  );
}
